import type { ActionContext, Module } from 'vuex'

import { TixTime } from '@/TixTime/TixTime'
import { CartApiResponse, fetchCart, fetchOwnCart } from '@/api/Cart'
import { apiEntities, firstEntity, linkTGRelations, mergeConfigIntoEvents } from '@/api/Helpers'
import type { EventWithConfig, LinkedTG, LinkedTT } from '@/api/types/processedEntities'
import CartAlreadyBookedError from '@/errors/CartAlreadyBookedError'
import NotFoundError from '@/errors/NotFoundError'
import { anchorsAreConfigured } from '@/helpers/Anchors'
import {
  ProcessedCompletedPayments,
  ProcessedScheduledPayment,
  fetchOrderAndCart,
  findStripeFee,
  getOverdueAmount,
  getUpcomingAmount,
  processCompletedPayment,
  processScheduledPayment,
  ticketsToCartItems,
} from '@/helpers/CartHelpers'
import { dictToMap } from '@/helpers/DictHelpers'
import { environment } from '@/helpers/Environment'
import { groupAsMap, groupBy, indexItemsById } from '@/helpers/IndexHelpers'
import { TimeInterval } from '@/helpers/Intervals'
import { sum } from '@/helpers/Math'
import { CartItem, TimedItem, isTimedItem } from '@/store/CartItem'

interface GiftCard {
  number: string
  balance: number
  state: string
}

interface CartState {
  localExpiry: number | null
  giftCards: GiftCard[]
  apiResponse: null | CartApiResponse
  visitWindow: null | {
    start: string
    end: string
    timeZone: string
  }
  hasCitypassCoupons: null | true
  allDaySessionIds: string[]
  giftee: null | Dictionary
  autoRenewMembership: boolean
  redeemMembership: boolean
  partialPaymentAmount: null | number
  declinedCartTimerExtension: boolean
}

interface Getters {
  giftee: null | Dictionary
  autoRenewMembership: boolean
  cartId?: string
  cartNumber?: string
  cart?: CartType
  cartMods?: CartMod[]
  blockingRules?: CartMod[]
  cartFees?: CartFee[]
  stripeFee?: CartFee
  stripeAccountId?: string
  giftCards: GiftCard[]
  localExpiry: number | null
  declinedCartTimerExtension: boolean
  visitWindow?: TimeInterval
  ticketCount: number
  totals: CartFee['grand_totals'] | 0
  faceValue: number
  totalFixedFees: number
  totalPercentFees: number
  totalPromoDiscounts: number
  totalPayments: number
  paymentDue: number
  giftCardBalance: number
  outstandingBalance: number
  paymentDueByCreditCard: number
  pendingGiftCardPayments: PendingGiftCardPayment[]
  identityFormConfigurations: string[]
  currentCartExists: boolean
  cartHasItems?: boolean
  cartItems: CartItem[]
  indexedLinkedTicketGroups?: Dict<LinkedTG>
  indexedLinkedTicketTypes?: Dict<LinkedTT>
  anchor?: CartItem
  tickets: Ticket[]
  injectedCartOwner?: Identity
  eventTemplates: EventTemplate[]
  eventsWithConfig: EventWithConfig[]
  ticketTypes: TicketType[]
  ticketGroups: TicketGroup[]
  linkedTicketGroups: LinkedTG[]
  uniqueDatesInCart: string[]
  allDaySessionIds: Set<string>
  scheduledPayments: ProcessedScheduledPayment[]
  completedPayments: ProcessedCompletedPayments[]
  timezone: string
  hasScheduledPayments: boolean
  flowDefinitions: Record<string, FlowDefinition>
  isModifiable: boolean
  ownedByAuthenticatedUser: boolean
}

// Only use a factory function to prevent state leaking into the default.
function defaultState(): CartState {
  return {
    localExpiry: null,
    giftCards: [],
    apiResponse: null,
    visitWindow: null,
    allDaySessionIds: [],
    hasCitypassCoupons: null,
    giftee: null,
    autoRenewMembership: false,
    redeemMembership: false,
    partialPaymentAmount: null,
    declinedCartTimerExtension: false,
  }
}

export interface CartType extends CartEntity {
  ownedByPublicIdentity: boolean
}

export interface PendingGiftCardPayment {
  paymentAmount: number
  cardNumber: string
  availableBalance: number
}

export default {
  namespaced: true,

  state: defaultState(),

  getters: {
    /**
     * @deprecated Use state.giftee.
     */
    giftee({ giftee }: CartState): Getters['giftee'] {
      return giftee
    },

    /**
     * @deprecated Use state.autoRenewMembership.
     */
    autoRenewMembership({ autoRenewMembership }: CartState): Getters['autoRenewMembership'] {
      return autoRenewMembership
    },

    cartId({ apiResponse: result }: CartState): Getters['cartId'] {
      if (result) {
        return result.cart._data[0].id
      }
    },

    cartNumber({ apiResponse: result }: CartState): Getters['cartNumber'] {
      if (result) {
        return result.cart._data[0].cart_number
      }
    },

    cart({ apiResponse: result }: CartState): Getters['cart'] {
      if (result) {
        const cart = result.cart._data[0] as CartEntity
        return {
          ...cart,
          ownedByPublicIdentity: cart.identity_id === environment.portal.public_identity_id,
        }
      }
    },

    cartMods({ apiResponse: result }: CartState): Getters['cartMods'] {
      return result?.cartmod._data
    },

    blockingRules({ apiResponse: result }: CartState): Getters['blockingRules'] {
      return result?.cartmod._data.filter(({ blocking_checkout }) => blocking_checkout)
    },

    cartFees({ apiResponse: result }: CartState): Getters['cartFees'] {
      return result?.cart_fees._data
    },

    stripeFee(_, { cartFees }: Getters): Getters['stripeFee'] {
      if (cartFees) {
        return findStripeFee(cartFees)
      }
    },

    stripeAccountId(_, { stripeFee }: Getters): Getters['stripeAccountId'] {
      return stripeFee?.account_id
    },

    /**
     * @deprecated Use state.giftCards.
     */
    giftCards(state: CartState): Getters['giftCards'] {
      return state.giftCards
    },

    localExpiry({ localExpiry }: CartState): Getters['localExpiry'] {
      return localExpiry
    },

    declinedCartTimerExtension({ declinedCartTimerExtension }: CartState): Getters['declinedCartTimerExtension'] {
      return declinedCartTimerExtension
    },

    visitWindow({ visitWindow }: CartState): Getters['visitWindow'] {
      if (visitWindow) {
        // Deserialize the TixTime objects.
        const { start, end, timeZone } = visitWindow
        return {
          start: new TixTime(start, timeZone),
          end: new TixTime(end, timeZone),
        }
      }
    },

    ticketCount(state: CartState, { cartItems }: Getters): Getters['ticketCount'] {
      const values = cartItems.map((item) => {
        const count = sum(item.types.map((type) => type.ticketCount))
        return item.isMembershipEvent ? 1 : count
      })
      return sum(values)
    },

    totals(_: CartState, { stripeFee }: Getters): Getters['totals'] {
      // NOTE: The ?? 0 fallback is intentional but potentially problematic.
      // When stripeFee is undefined (no cart), we return 0 instead of a grand_totals object.
      // This forces downstream getters to check `totals === 0` before accessing properties.
      // TODO Return undefined/void instead?
      return stripeFee?.grand_totals ?? 0
    },

    // After promo codes (cartmod).
    faceValue(state: CartState, { totals }: Getters): Getters['faceValue'] {
      return totals === 0 ? 0 : parseFloat(totals.total_before_cartmod_price)
    },

    totalFixedFees(state: CartState, { totals }: Getters): Getters['totalFixedFees'] {
      return totals === 0 ? 0 : parseFloat(totals.total_fee_fixed_outside)
    },

    totalPercentFees(state: CartState, { totals }: Getters): Getters['totalPercentFees'] {
      return totals === 0 ? 0 : parseFloat(totals.total_fee_percent_outside)
    },

    totalPromoDiscounts(state: CartState, { totals }: Getters): Getters['totalPromoDiscounts'] {
      return totals === 0 ? 0 : parseFloat(totals.total_cartmod_amount)
    },

    totalPayments(state: CartState, { totals }: Getters): Getters['totalPayments'] {
      return totals === 0 ? 0 : parseFloat(totals.total_payments)
    },

    paymentDue(state: CartState, { totals }: Getters): Getters['paymentDue'] {
      return totals === 0 ? 0 : parseFloat(totals.total_outstanding)
    },

    giftCardBalance({ giftCards }: CartState): Getters['giftCardBalance'] {
      return sum(giftCards.map((card) => card.balance))
    },

    outstandingBalance(_: CartState, { paymentDue, giftCardBalance }: Getters): Getters['outstandingBalance'] {
      return Math.max(0, paymentDue - giftCardBalance)
    },

    paymentDueByCreditCard(
      { partialPaymentAmount }: CartState,
      { outstandingBalance, hasScheduledPayments, scheduledPayments }: Getters,
    ): Getters['paymentDueByCreditCard'] {
      if (hasScheduledPayments) {
        return partialPaymentAmount ?? getUpcomingAmount(scheduledPayments) + getOverdueAmount(scheduledPayments)
      } else {
        return outstandingBalance
      }
    },

    pendingGiftCardPayments({ giftCards }: CartState, { paymentDue }: Getters): Getters['pendingGiftCardPayments'] {
      let remainder = paymentDue
      return giftCards.map((card) => {
        const amount = Math.min(remainder, card.balance)
        remainder -= amount
        return {
          cardNumber: card.number,
          availableBalance: card.balance,
          paymentAmount: amount,
          remainingBalance: card.balance - amount,
        }
      })
    },

    identityFormConfigurations({ apiResponse }: CartState): Getters['identityFormConfigurations'] {
      if (apiResponse) {
        return apiResponse.meta._data
          .filter(({ metakey, resource }) => {
            // Legacy support.
            if (resource === 'ticket_group' && metakey === 'extra_checkout_fields') {
              return true
            } else if (resource === 'ticket_group' || resource === 'event_template') {
              return metakey === 'identity_form'
            }
          })
          .map((meta) => meta.value)
      } else {
        return []
      }
    },

    currentCartExists(_: CartState, { cart, localExpiry }: Getters): Getters['currentCartExists'] {
      if (!cart) {
        return false
      }

      if (!localExpiry) {
        // Held carts don't have a localExpiry because they weren't created by the web UI.
        return true
      }

      return new TixTime(localExpiry).isAfter(new TixTime())
    },

    cartHasItems({ apiResponse: result }: CartState): Getters['cartHasItems'] {
      return result ? result.ticket._data.length > 0 : undefined
    },

    cartItems({ apiResponse }: CartState, getters: Getters): Getters['cartItems'] {
      if (!apiResponse || !getters.currentCartExists) {
        return []
      }

      const entities = apiEntities(apiResponse)
      const sessions = indexItemsById(entities.event_session)
      const templates = indexItemsById(getters.eventsWithConfig)

      // Group tickets by session.
      const bySession = groupBy('event_session_id', entities.ticket)

      // Group the session-less tickets by template. E.g. Membership.
      const byTemplate = groupAsMap(bySession.null || [], 'event_template_id', templates)

      // Remove the session-less tickets from the tickets grouped by session.
      delete bySession.null

      // Then build a Map from the remaining sessions.
      const sessionGroups = dictToMap(bySession, sessions)

      // Store various lookup tables for re-use later.
      const data = {
        templates,
        allDaySessionIds: getters.allDaySessionIds,
        ticketTypes: getters.indexedLinkedTicketTypes!,
        ticketGroups: getters.indexedLinkedTicketGroups!,
        venues: indexItemsById(entities.venue),
      }

      const untimed = ticketsToCartItems(byTemplate, data)
      const timed = (ticketsToCartItems(sessionGroups, data) as TimedItem[])
        // TODO Use event template rank for secondary sort.
        // .sort((a, b) => (a.event._rank - b.event._rank))
        .sort((a, b) => (a.startTime.isBefore(b.startTime) ? -1 : 1))

      const memberships = untimed.filter((item) => item.isMembershipEvent)
      const otherUntimed = untimed.filter((item) => !item.isMembershipEvent)

      // Data-layer ordering (display layer reorders flow groups ahead of non-flow timed items):
      // 1. Memberships
      // 2. Cart items with sessions
      // 3. Other cart items without sessions
      return [...memberships, ...timed, ...otherUntimed]
    },

    indexedLinkedTicketGroups(_: CartState, { linkedTicketGroups }: Getters): Getters['indexedLinkedTicketGroups'] {
      return indexItemsById(linkedTicketGroups)
    },

    indexedLinkedTicketTypes(_: CartState, { linkedTicketGroups }: Getters): Getters['indexedLinkedTicketTypes'] {
      return indexItemsById(linkedTicketGroups.flatMap((group) => group.types))
    },

    anchor(_: CartState, { cartItems }: Getters): Getters['anchor'] {
      // There are two definitions of anchors, depending on whether upsell configuration
      // specifies anchor events or allows any event to be an anchor;
      if (anchorsAreConfigured()) {
        return cartItems.find((item) => item.isAnchor)
      } else {
        // If no anchors are configured, use the first timed cart item as an anchor.
        return cartItems.find(isTimedItem)
      }
    },

    tickets({ apiResponse: result }: CartState): Getters['tickets'] {
      return result?.ticket._data ?? []
    },

    /**
     * @deprecated Identity does not indicate the cart is injected and it is not necessarily the owner.
     */
    injectedCartOwner({ apiResponse: result }: CartState): Getters['injectedCartOwner'] {
      return result?.identity._data[0]
    },

    eventTemplates({ apiResponse: result }: CartState): Getters['eventTemplates'] {
      return result?.event_template._data ?? []
    },

    eventsWithConfig({ apiResponse }: CartState): Getters['eventsWithConfig'] {
      if (apiResponse) {
        return mergeConfigIntoEvents(apiResponse.event_template._data, apiResponse)
      } else {
        return []
      }
    },

    ticketTypes({ apiResponse: result }: CartState): Getters['ticketTypes'] {
      return result!.ticket_type._data
    },

    ticketGroups({ apiResponse: result }: CartState): Getters['ticketGroups'] {
      return result?.ticket_group._data ?? []
    },

    linkedTicketGroups({ apiResponse }: CartState): Getters['linkedTicketGroups'] {
      if (apiResponse) {
        const data = apiEntities(apiResponse)
        return linkTGRelations(data.ticket_group, data.meta, data.ticket_type)
      } else {
        return []
      }
    },

    // flow_definition is auto-included by the API when the cart has flow tickets (not via embed).
    // Non-flow carts won't have it, so the guard below returns {}.
    flowDefinitions({ apiResponse }: CartState): Getters['flowDefinitions'] {
      if (!apiResponse?.flow_definition) return {}

      const data = apiEntities(apiResponse).flow_definition
      if (!data) return {}

      const result: Record<string, FlowDefinition> = {}
      for (const def of data) {
        result[def.id] = def
      }
      return result
    },

    uniqueDatesInCart(_: CartState, { cartItems }: Getters): Getters['uniqueDatesInCart'] {
      return [...new Set(cartItems.filter(isTimedItem).map((item) => item.startTime.format('DATE')))]
    },

    allDaySessionIds({ allDaySessionIds }: CartState): Getters['allDaySessionIds'] {
      return new Set(allDaySessionIds)
    },

    scheduledPayments({ apiResponse }: CartState, { timezone }: Getters): Getters['scheduledPayments'] {
      if (!apiResponse) {
        return []
      }

      return apiEntities(apiResponse).scheduled_payment.map((payment) => processScheduledPayment(payment, timezone))
    },

    completedPayments({ apiResponse }: CartState, { timezone }: Getters): Getters['completedPayments'] {
      if (!apiResponse) {
        return []
      }

      return apiEntities(apiResponse).gateway_audit.map((payment) => processCompletedPayment(payment, timezone))
    },

    timezone({ apiResponse }: CartState): Getters['timezone'] {
      // Seller will always be present once stale carts have expired from localStorage.
      // TODO Remove optional chaining on `seller?`.
      return apiResponse?.seller?._data[0]?.timezone ?? environment.portal.timezone
    },

    hasScheduledPayments(_: CartState, { scheduledPayments }: Getters): Getters['hasScheduledPayments'] {
      return scheduledPayments.length > 0
    },

    /**
     * @see https://docs.google.com/spreadsheets/d/1L3OMbW1Tc3CHpx3GgkIXvVRpzXamIEtdtqlUzHV4vYw/edit?usp=sharing
     */
    isModifiable(_: CartState, { cart, ownedByAuthenticatedUser }: Getters): Getters['isModifiable'] {
      if (cart && cart.state === 'pending') {
        return cart.ownedByPublicIdentity || ownedByAuthenticatedUser
      } else {
        return false
      }
    },

    ownedByAuthenticatedUser(_: CartState, { cart }: Getters, __, rootGetters): Getters['ownedByAuthenticatedUser'] {
      const identity = rootGetters['Member/identityWithMeta'][0]
      return identity && cart ? identity.id === cart.identity_id : false
    },
  },

  mutations: {
    giftee(state, value) {
      state.giftee = value
    },

    localExpiry(state, value) {
      state.localExpiry = value
    },

    // Only used by clearAll.
    giftCards(state, cards) {
      state.giftCards = cards
    },

    addGiftCard(state, giftCard) {
      state.giftCards.push(giftCard)
    },

    removeGiftCard(state, number) {
      state.giftCards = state.giftCards.filter((card) => number !== card.number)
    },

    apiResponse(state, result) {
      state.apiResponse = result
    },

    allDaySessionIds(state, result) {
      state.allDaySessionIds = result
    },

    hasCitypassCoupons(state, value) {
      state.hasCitypassCoupons = value
    },

    visitWindow(state, value) {
      if (value) {
        const { start, end } = value
        // Serialize the Tix Time objects.
        state.visitWindow = {
          start: start.format('iso'),
          end: end.format('iso'),
          timeZone: start.timezone,
        }
      } else {
        state.visitWindow = null
      }
    },

    autoRenewMembership(state, value) {
      state.autoRenewMembership = value
    },

    redeemMembership(state, value) {
      state.redeemMembership = value
    },

    partialPaymentAmount(state, value) {
      state.partialPaymentAmount = value
    },

    declinedCartTimerExtension(state, value) {
      state.declinedCartTimerExtension = value
    },
  },

  actions: {
    addAllDaySession(context: ActionContext<CartState, CartState>, id) {
      context.commit('allDaySessionIds', [id, ...context.state.allDaySessionIds])
    },

    clearAll(context: ActionContext<CartState, CartState>) {
      // Do not clear legacy properties that are no longer supported.
      const state = defaultState()
      for (const key in state) {
        context.commit(key, state[key])
      }
    },

    // This is used for payment links and cart hand-off. Scenarios 3-6 in the checkout matrix;
    // https://docs.google.com/spreadsheets/d/1ADBr3cildyuewCLiMdCxYty8GMZpc1NpME9Urh18PbA/edit?usp=sharing
    inject(context: ActionContext<CartState, CartState>, id) {
      return fetchCart(id).then((response) => {
        context.dispatch('injectResponse', response)
      })
    },

    // This is used for cart hand-off, similar to inject() above but for carts owned by the authenticated user.
    injectOwnCart(context: ActionContext<CartState, CartState>, id) {
      return fetchOwnCart(id).then((response) => {
        context.dispatch('injectResponse', response)
      })
    },

    injectResponse(context: ActionContext<CartState, CartState>, response: CartApiResponse) {
      const [cart] = firstEntity(response, 'cart')
      const { cart_fees: fees, ticket: tickets } = apiEntities(response)
      const fee = findStripeFee(fees)
      const outstandingBalance = parseFloat(fee.grand_totals.total_outstanding)
      const hasHeldTickets = tickets.some((t) => t.state === 'held')

      if (cart.state === 'expired') {
        throw new NotFoundError('cart', cart)
      } else if (cart.expires_at == null && !hasHeldTickets && outstandingBalance <= 0) {
        const [ticket] = firstEntity(response, 'ticket')
        throw new CartAlreadyBookedError(ticket.ticket_order_id!)
      } else {
        context.dispatch('clearAll')
        context.commit('apiResponse', response)
      }
    },

    // <EditOrderRoute> uses this Scenarios 7-8 in the checkout matrix;
    // https://docs.google.com/spreadsheets/d/1L3OMbW1Tc3CHpx3GgkIXvVRpzXamIEtdtqlUzHV4vYw/edit?usp=sharing
    injectOrder(context: ActionContext<CartState, CartState>, orderId: string) {
      return fetchOrderAndCart(orderId).then(({ cart: response }) => {
        // TODO Assert the cart is booked?
        // response.cart._data[0].state === 'booked'
        context.dispatch('clearAll')
        context.commit('apiResponse', response)
      })
    },
  },
} as Module<CartState, CartState>
